// components/booking/ServiceConfirmationModal.js
import React, { useState, useEffect, useCallback } from 'react';
import { Check, X, ArrowRight, Car, Info, Trash2, Loader } from 'lucide-react';
import { calculateVehiclePrice, VEHICLE_SIZE_CATEGORIES } from '../../utils/vehiclePricing';

const ServiceConfirmationModal = ({ 
  serviceDetails, 
  vehicleDetails,
  onConfirm, 
  onCancel,
  onChangeService,
  onRemoveVehicle,
  isLoadingVehicle = false,
  onPriceChange = null
}) => {
  const [pricingInfo, setPricingInfo] = useState({
    price: serviceDetails?.price || 0,
    vehicleSize: 'STANDARD',
    multiplier: 1,
    sizeDescription: VEHICLE_SIZE_CATEGORIES.STANDARD.description
  });
  const [showPricingInfo, setShowPricingInfo] = useState(false);
  
  // Calculate price based on vehicle when vehicle changes
  // But don't call onPriceChange in the effect to avoid infinite loops
  useEffect(() => {
    if (serviceDetails && vehicleDetails?.vehicle) {
      // Store original price if not already set
      const basePrice = serviceDetails.originalPrice || serviceDetails.price;
      
      const calculatedPricing = calculateVehiclePrice(
        basePrice, 
        vehicleDetails.vehicle
      );
      
      // For STANDARD or COMPACT vehicles, override the price to use the original price
      // This ensures standard/compact vehicles don't get marked up
      if (calculatedPricing.vehicleSize === 'STANDARD' || calculatedPricing.vehicleSize === 'COMPACT') {
        calculatedPricing.price = basePrice;
        calculatedPricing.multiplier = 1;
      }
      
      setPricingInfo(calculatedPricing);
    } else if (serviceDetails) {
      // Default pricing if no vehicle selected
      setPricingInfo({
        price: serviceDetails.price,
        vehicleSize: 'STANDARD',
        multiplier: 1,
        sizeDescription: VEHICLE_SIZE_CATEGORIES.STANDARD.description
      });
    }
  }, [serviceDetails, vehicleDetails]);
  
  // Handle vehicle removal with event stopping
  const handleRemoveVehicle = useCallback((e) => {
    // Prevent event bubbling
    e.preventDefault();
    e.stopPropagation();
    
    // Call the parent handler
    if (onRemoveVehicle) {
      onRemoveVehicle();
    }
  }, [onRemoveVehicle]);
  
  // Toggle pricing info display
  const togglePricingInfo = useCallback(() => {
    setShowPricingInfo(prev => !prev);
  }, []);
  
  // Handle continue button click
  const handleContinue = useCallback(() => {
    // Only call onPriceChange if the price differs from base price AND the vehicle is not standard/compact
    if (onPriceChange && 
        pricingInfo.price !== serviceDetails.price && 
        pricingInfo.vehicleSize !== 'STANDARD' && 
        pricingInfo.vehicleSize !== 'COMPACT') {
      onPriceChange(pricingInfo.price, pricingInfo);
    } else if (onPriceChange && 
               (pricingInfo.vehicleSize === 'STANDARD' || pricingInfo.vehicleSize === 'COMPACT')) {
      // For standard/compact vehicles, explicitly pass the original price to reset any previous adjustments
      const basePrice = serviceDetails.originalPrice || serviceDetails.price;
      onPriceChange(basePrice, {
        ...pricingInfo,
        price: basePrice,
        multiplier: 1
      });
    }
    
    // Always confirm with the appropriate price
    const confirmPrice = (pricingInfo.vehicleSize === 'STANDARD' || pricingInfo.vehicleSize === 'COMPACT')
      ? (serviceDetails.originalPrice || serviceDetails.price)
      : pricingInfo.price;
      
    onConfirm(confirmPrice);
  }, [onPriceChange, pricingInfo, serviceDetails, onConfirm]);
  
  if (!serviceDetails) return null;
  
  // Format features for display
  const formatFeatures = (features) => {
    if (Array.isArray(features)) {
      return features;
    } else if (typeof features === 'object') {
      return Object.entries(features).flatMap(([category, items]) => {
        return items.map(item => `${item} (${category})`);
      });
    }
    return [];
  };
  
  const displayFeatures = formatFeatures(serviceDetails.features);
  
  // Get category display name
  const getCategoryDisplay = (category) => {
    const categories = {
      'full': 'Full Detail',
      'exterior': 'Exterior Only',
      'interior': 'Interior Only',
      'ceramic': 'Ceramic Coating'
    };
    return categories[category] || category;
  };
  
  // Format vehicle name if available
  const getVehicleDisplay = () => {
    // If loading vehicles, show loading state
    if (isLoadingVehicle) {
      return (
        <div className="mt-4 p-3 bg-gray-50 rounded-lg">
          <div className="flex items-center">
            <Loader className="h-4 w-4 mr-2 text-blue-500 animate-spin" />
            <p className="text-sm text-gray-600">Loading your vehicle...</p>
          </div>
        </div>
      );
    }
    
    if (!vehicleDetails?.vehicle) {
      return null;
    }
    
    const { make, model, year, color } = vehicleDetails.vehicle;
    
    if (make && model) {
      return (
        <div className="mt-4 p-3 bg-gray-50 rounded-lg">
          <div className="flex justify-between items-start">
            <div>
              <h4 className="text-sm font-medium text-gray-700 flex items-center">
                <Car className="h-4 w-4 mr-2 text-blue-500" />
                Vehicle Details
              </h4>
              <div className="mt-1">
                <p className="text-sm text-gray-800">
                  {year} {make} {model} {color && `(${color})`}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  Vehicle size: {pricingInfo.sizeDescription}
                </p>
              </div>
            </div>
            
            {/* Remove vehicle button */}
            {onRemoveVehicle && (
              <button
                onClick={handleRemoveVehicle}
                className="text-red-500 hover:text-red-700 p-1 rounded-full hover:bg-red-50 transition-colors"
                title="Remove vehicle"
                type="button"
                aria-label="Remove vehicle"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
      );
    }
    
    return null;
  };

  // Only show price info if there's a vehicle and it's not standard/compact
  const shouldShowPriceAdjustment = 
    pricingInfo.multiplier !== 1 && 
    pricingInfo.vehicleSize !== 'STANDARD' && 
    pricingInfo.vehicleSize !== 'COMPACT';

  // Get the price to display
  const displayPrice = (pricingInfo.vehicleSize === 'STANDARD' || pricingInfo.vehicleSize === 'COMPACT')
    ? (serviceDetails.originalPrice || serviceDetails.price)
    : pricingInfo.price;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-2xl font-bold text-gray-800">Confirm Your Selection</h2>
            <button 
              onClick={onCancel}
              className="p-1 rounded-full hover:bg-gray-100"
              type="button"
              aria-label="Close"
            >
              <X className="h-6 w-6 text-gray-500" />
            </button>
          </div>
          
          <div className="mb-4">
            <span className="bg-blue-100 text-blue-800 text-xs px-3 py-1 rounded-full font-medium">
              {getCategoryDisplay(serviceDetails.category)}
            </span>
          </div>
          
          <h3 className="text-xl font-bold text-gray-800 mb-2">{serviceDetails.title}</h3>
          <p className="text-gray-600 mb-2">{serviceDetails.description}</p>
          
          {/* Price with vehicle adjustment */}
          <div className="flex items-center">
            <p className="text-xl font-semibold text-blue-600 mr-2">${displayPrice}</p>
            
            {shouldShowPriceAdjustment && (
              <button 
                onClick={togglePricingInfo}
                className="text-xs text-blue-600 flex items-center hover:underline"
                type="button"
              >
                <Info className="h-3 w-3 mr-1" />
                Price adjusted for vehicle size
              </button>
            )}
          </div>
          
          {showPricingInfo && shouldShowPriceAdjustment && (
            <div className="mt-2 p-3 bg-blue-50 rounded text-sm text-gray-700">
              <p>Base price: ${serviceDetails.originalPrice || serviceDetails.price}</p>
              <p>Vehicle size: {pricingInfo.sizeDescription}</p>
              <p>Multiplier: {pricingInfo.multiplier}x</p>
            </div>
          )}
          
          {/* Display vehicle if available or loading */}
          {getVehicleDisplay()}
          
          {/* Option to add vehicle if not present and not loading */}
          {!vehicleDetails?.vehicle && !isLoadingVehicle && (
            <div className="mt-4 p-3 bg-gray-50 rounded-lg border border-gray-200 border-dashed">
              <p className="text-sm text-gray-600 flex items-center">
                <Car className="h-4 w-4 mr-2 text-gray-400" />
                No vehicle selected. You'll be able to provide vehicle details in the next steps.
              </p>
            </div>
          )}
        </div>
        
        <div className="p-6">
          <h4 className="font-semibold mb-4 text-gray-700">What's Included:</h4>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6">
            {displayFeatures.map((feature, index) => (
              <div key={index} className="flex items-start">
                <Check className="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                <span className="text-sm text-gray-700">{feature}</span>
              </div>
            ))}
          </div>
          
          <div className="flex justify-between pt-4 border-t border-gray-200">
            <button 
              onClick={onChangeService}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-600 hover:bg-gray-50 transition-all duration-200"
              type="button"
            >
              Choose Different Service
            </button>
            
            <button 
              onClick={handleContinue}
              className="px-6 py-2 bg-gradient-to-r from-blue-500 to-cyan-400 text-white rounded-lg hover:shadow-md transition-all duration-200 flex items-center"
              type="button"
            >
              Continue to Schedule
              <ArrowRight className="ml-2 h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ServiceConfirmationModal;